import { createSlice } from '@reduxjs/toolkit';
import { set } from 'lodash';

const initialState = {
  isLoading: false,
  error: false,
  campaigns: [],
  totalCampaigns: 0,
  /** Last successful GET /campaigns query (1-based page) — used to refetch after mutations */
  listQuery: { page: 1, limit: 20, search: '' },
  campaignById: null,
  qrGenerationLoading: false,
  /** { message: string, QRCodes: object } from POST generate QR response */
  qrGenerationResult: null,
  qrDownloadLoading: false,
  /** Set after successful zip download; consumed for snackbar */
  qrDownloadToast: null,
  /** Campaign QR list page (GET /qrs/campaign/:id) */
  qrListLoading: false,
  qrListRows: [],
  qrListTotal: 0,
  /** Single QR from GET /qrs/:qrCode (drawer detail) */
  qrDetail: null,
  qrDetailLoading: false,

  /**  GET payments */
  paymentData: [],
  paymentsLoading: false,

  /**  add payments payment */
  startAddPaymentsLoading: false,
  addPaymentsSuccess: [],
  addPaymentsFailed: false,

  /** Payment batch CSV (tracker API) from Campaign QR page */
  paymentBatchCsvLoading: false,
  paymentBatchCsvToast: null,
};

const campaignSlice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.isLoading = action?.payload ?? true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    hasError(state) {
      state.error = true;
      state.isLoading = false;
    },
    getCampaignsSuccess(state, action) {
      state.campaigns = action.payload || [];
      state.error = false;
      state.isLoading = false;
    },
    setTotalCampaigns(state, action) {
      state.totalCampaigns = action.payload ?? 0;
    },
    setCampaignListQuery(state, action) {
      state.listQuery = { ...state.listQuery, ...action.payload };
    },
    getCampaignByIdSuccess(state, action) {
      state.campaignById = action.payload || null;
      state.error = false;
    },
    deleteCampaignSuccess(state, action) {
      const id = action.payload;
      state.campaigns = (state.campaigns || []).filter((c) => c?.id !== id);
    },
    startQrGeneration(state) {
      state.qrGenerationLoading = true;
      state.qrGenerationResult = null;
    },
    failQrGeneration(state) {
      state.qrGenerationLoading = false;
    },
    setQrGenerationSuccess(state, action) {
      state.qrGenerationLoading = false;
      state.qrGenerationResult = action.payload;
    },
    clearQrGenerationResult(state) {
      state.qrGenerationResult = null;
    },
    startQrDownload(state) {
      state.qrDownloadLoading = true;
    },
    failQrDownload(state) {
      state.qrDownloadLoading = false;
    },
    finishQrDownload(state, action) {
      state.qrDownloadLoading = false;
      if (action.payload?.toast) {
        state.qrDownloadToast = action.payload.toast;
      }
    },
    clearQrDownloadToast(state) {
      state.qrDownloadToast = null;
    },
    startQrListLoading(state) {
      state.qrListLoading = true;
    },
    setQrListSuccess(state, action) {
      state.qrListLoading = false;
      state.qrListRows = action.payload?.rows || [];
      state.qrListTotal = action.payload?.total ?? 0;
    },
    failQrList(state) {
      state.qrListLoading = false;
    },
    startQrDetailLoading(state) {
      state.qrDetailLoading = true;
      state.qrDetail = null;
    },
    setQrDetailSuccess(state, action) {
      state.qrDetailLoading = false;
      state.qrDetail = action.payload ?? null;
    },
    failQrDetail(state) {
      state.qrDetailLoading = false;
    },
    clearQrDetail(state) {
      state.qrDetail = null;
      state.qrDetailLoading = false;
    },
    setPaymentData(state, action) {
      state.paymentData = action.payload || [];
    },
    startPaymentBatchCsv(state) {
      state.paymentBatchCsvLoading = true;
    },
    failPaymentBatchCsv(state) {
      state.paymentBatchCsvLoading = false;
    },
    finishPaymentBatchCsv(state, action) {
      state.paymentBatchCsvLoading = false;
      if (action.payload?.toast) {
        state.paymentBatchCsvToast = action.payload.toast;
      }
    },
    clearPaymentBatchCsvToast(state) {
      state.paymentBatchCsvToast = null;
    },
  },
});

export default campaignSlice.reducer;
export const {
  startLoading,
  stopLoading,
  hasError,
  getCampaignsSuccess,
  setTotalCampaigns,
  setCampaignListQuery,
  getCampaignByIdSuccess,
  deleteCampaignSuccess,
  startQrGeneration,
  failQrGeneration,
  setQrGenerationSuccess,
  clearQrGenerationResult,
  startQrDownload,
  failQrDownload,
  finishQrDownload,
  clearQrDownloadToast,
  startQrListLoading,
  setQrListSuccess,
  failQrList,
  startQrDetailLoading,
  setQrDetailSuccess,
  failQrDetail,
  clearQrDetail,
  setPaymentData,
  startPaymentBatchCsv,
  failPaymentBatchCsv,
  finishPaymentBatchCsv,
  clearPaymentBatchCsvToast,
} = campaignSlice.actions;
